<!--
* A compund component to allow user to choose the replacemtn item they would like
*
* This component wraps an image, checkbox and option chooser in one control
* 
* @param {string} uncheckedSrc - Image source url for the unchecked image
* @param {string} checkedSrc - Image source url for the checked image
* @param {boolean} isCheckedOnInit - Whether the checkbox should be checked on init
*
* @fires appReplacementItem#selected
-->

<!--
* @event appReplacementItem#selected
* @property {boolean} checked  Whether the the checkbox is checked
-->
<!--
  @todo update v-model rather than allowing initial checked state
   and emiting event
-->
<template>
  <div class="app_replacement_item"
       v-bind:class="{ selected: selected }">
    <div class="app_replacement_item__wrapper"        
        @click.self="onClick">
      <!-- photo box -->
      <div class="app_replacement_item__image_wrapper"
          v-if="image">
        <img class="app_replacement_item__image" @click.self="onClick"
            v-bind:src="image" />
      </div>
      <img @click="openDisclaimer" class="app_replacement_item__disclaimer_icon"
        src="/static/theme/img/info-icon.svg"
        :alt="disclaimerHeader">

      <!-- caption and checkbox -->
      <div class="app_replacement_item__footer"
          v-bind:class="{ app_replacement_item__footer_border: !image }">
        <!-- checkbox -->
        <app-checkbox
          :uncheckedSrc="uncheckedSrc"
          :checkedSrc="checkedSrc"
          ref="checkbox"
          :selected="selected"
          @change="onCheckboxChange" />
        <!-- Caption -->
        <div
          class="form__image_caption">{{caption}}</div>
      </div>
    
    </div>
     <!-- colour dropdown -->
    <appSelect 
      v-if="selected && selectOptions && selectOptions.length > 0"
      class="select is-fullwidth"
      :options="selectOptions"
      :display-key="selectDisplayKey"
      v-model="selectedValue"
      :selected="selectVal"
      :placeholder="selectPlaceholder"/>

    <!-- alternative colours dropdown -->
      <appSelectMultiple 
        v-if="selected && selectedValue != null && selectAltColourOptionsFiltered.length > 0"
        :options="selectAltColourOptionsFiltered" 
        :placeholder="selectAltColourPlaceholder" 
        :hasPlaceholder="true"
        :valueKey="'id'" 
        :nameKey="'value'"
        v-model="selectedAltColourValue" 
        /> 

    <!-- App message to disclaimer info -->
    <ms-app-message
      :title="disclaimerHeader"
      :isOpen.sync="showDisclaimer">
      <span>
        <p>{{ disclaimerText }}</p>
      </span>
    </ms-app-message>

  </div>
</template>

<script>
  import AppSelect from '@/components/app-select'
  import AppSelectMultiple from '@/components/app-select-multiple'
  import MsAppMessage from '@/components/ms-app-message'

  export default {
    components: {
      AppSelect,
      AppSelectMultiple,
      MsAppMessage
    },
    props: {
      selected: {
        required: true,
        type: Boolean
      },
      uncheckedSrc: {
        required: true,
        type: String
      },

      checkedSrc: {
        required: true,
        type: String
      },

      caption: {
        type: String
      },

      image: {
        type: String
      },
  
      selectVal: {
        type: Number
      },

      selectDisplayKey: {
        type: String
      },

      selectOptions: {
        type: Array
      },

      selectPlaceholder: {
        type: String
      },
      // array of indexes
      selectAltColourVal: {
        type: Array
      },

      selectAltColourDisplayKey: {
        type: String
      },

      selectAltColourOptions: {
        type: Array
      },

      selectAltColourPlaceholder: {
        type: String
      },

      disclaimerHeader: {
        type: String
      },
      disclaimerText: {
        type: String
      }
    },
    data: function () {
      return {
        showDisclaimer: false,
        // filtered list which will contain colours except
        // main colour picked in separate dropdown
        selectAltColourOptionsFiltered: this.selectAltColourOptions
      }
    },
    methods: {
      onCheckboxChange: function (selected) {
        this.$emit('update:selected', selected)
        this.$emit('change', selected)
      },
      onClick: function () {
        let checkbox = this.$refs.checkbox
        checkbox.onClick()
      },
      /**
       * Open disclaimer info message popup
       */
      openDisclaimer: function () {
        this.showDisclaimer = true
      }
    },
    computed: {
      selectedValue: {
        get () {
          return this.selectVal
        },

        set (value) {
          const val = value !== null ? value.index : value
          this.$emit('update:selectVal', val)

          this.selectAltColourOptionsFiltered = this.selectAltColourOptions.filter(item => item.id !== value.id)
          // clear alt colours
          this.selectedAltColourValue = []
        }
      },
      // indexes
      selectedAltColourValue: {
        get () {
          return this.selectAltColourVal
        },

        set (value) {
          if (value === null) {
            this.$emit('update:selectAltColourVal', [])
          }
          // get indexes of selected options
          var selectedIndexes = []
          value.forEach(element => {
            let myIndex = this.selectAltColourOptions.findIndex(x => x === element)
            selectedIndexes.push(myIndex)
          })
          this.$emit('update:selectAltColourVal', selectedIndexes)
        }
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "../assets/sass/shared.scss";

  .app_replacement_item {
    cursor: pointer;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  }

  // keep the highlighted border if view is selected
  .app_replacement_item.selected, .app_replacement_item:hover {
    border: $secondary-border-style;
  }

  .app_replacement_item__image_wrapper {
    display: flex;
    width: 100%;
    background: $quaternary-brand-color;
    border: $default-border-style;
    border-bottom: 0;
    font-size: $medium-font-size;
  }

  .app_replacement_item__disclaimer_icon{
    float: right;
    width: $dims-spacing-large;
    height: $dims-spacing-large;
    margin-right: $dims-spacing-small;
    margin-top: -$dims-spacing-xlarge;
  }

  .app_replacement_item__image {
    display: block;
    margin-left: auto;
    margin-right: auto;
    user-select: none;
    height: $dims-image-checkbox-height;
  }

  .app_replacement_item__footer {
    pointer-events: none;
    display: flex;
    width: 100%;
    background: $secondary-brand-color;
    padding: $dims-spacing-small;
    border: $default-border-style;
    font-size: $medium-font-size;
  }

  .form__image_caption {
    display: block;
    width: 100%;
    padding-left: $dims-spacing-small;
    font-size: $medium-font-size;
  }

  .select.is-fullwidth {
    height: auto;

    ::v-deep .select {
      height: auto;

      select {
        border: $default-border-style;
        border-top: 0;
        height: auto;
      }
    }
  }
</style>
